import { Injectable, computed, signal } from '@angular/core';
import { IsoDate, QueryFilter } from '@softline/core';
import { AbcAnalysisBaseMeasure } from '../types/abc-analysis-base-measure';
import { AbcAnalysisOverviewMaster } from '../types/abc-analysis-master';
import { AbcAnalysisOverviewRow } from '../types/abc-analysis-overview-row';

function startOfLastMonth(): IsoDate {
  const now = new Date();
  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return `${lastMonth.getFullYear()}-${String(lastMonth.getMonth() + 1).padStart(2, '0')}-01` as IsoDate;
}

function endOfLastMonth(): IsoDate {
  const now = new Date();
  const lastDayOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
  return lastDayOfLastMonth.toISOString().slice(0, 10) as IsoDate;
}

// Path-parameter state for the abc-analysis endpoints (baseMeasure/abcAnalysisId), plus the
// date range — kept outside the pageFilter FilterState (SOFTLINE_FILTER_WWS_ABC_ANALYSIS,
// see abc-analysis-query.filter.ts) on purpose, mirroring cinema's pattern
// (CinemaStatisticsMenuBarComponent keeps its date range as its own control too): the data
// services combine dateRangeFilter with the pageFilter FilterState's queryFilter() via two
// QueryBuilder.filter() calls (auto-ANDed).
@Injectable()
export class AbcAnalysisFilterStateService {
  readonly baseMeasure = signal<AbcAnalysisBaseMeasure>('CONTRIBUTION_MARGIN');
  readonly abcAnalysisId = signal<number>(1);

  readonly dateFrom = signal<IsoDate>(startOfLastMonth());
  readonly dateTo = signal<IsoDate>(endOfLastMonth());

  // The backend requires a dateInRange filter on period on every request. ABC rows have no
  // statistics. facade — that prefix is only for sales/purchase document-line data.
  readonly dateRangeFilter = computed<QueryFilter<AbcAnalysisOverviewRow>>(
    () =>
      ({
        type: 'dateInRange',
        name: 'period',
        valueFrom: this.dateFrom(),
        valueTo: this.dateTo(),
      }) as unknown as QueryFilter<AbcAnalysisOverviewRow>
  );

  readonly master = computed<AbcAnalysisOverviewMaster>(() => ({
    baseMeasure: this.baseMeasure(),
    abcAnalysisId: this.abcAnalysisId(),
  }));
}
